import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, Trash2, MailOpen, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { updateQuote } from "../../../lib/adminApi";

const STATUS = ["yeni", "görüşülüyor", "onaylandı", "reddedildi"];
const STATUS_STYLE = {
  "yeni": "bg-[#B09060]/15 text-[#8F7648]",
  "görüşülüyor": "bg-blue-50 text-blue-700",
  "onaylandı": "bg-emerald-50 text-emerald-700",
  "reddedildi": "bg-red-50 text-red-700",
};
const TYPE_LABELS = { teklif: "Teklif", kurumsal: "Kurumsal", iletisim: "İletişim" };

export default function QuotesSection() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("quote_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(`Teklifler yüklenemedi: ${error.message}`);
    else setQuotes(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function patch(q, changes) {
    try {
      const updated = await updateQuote(q.id, changes);
      setQuotes((qs) => qs.map((x) => (x.id === q.id ? { ...x, ...updated } : x)));
      toast.success("Güncellendi.");
    } catch {
      toast.error("Güncelleme başarısız. (status/admin_notes sütunları için migration gerekli)");
    }
  }

  async function remove(q) {
    if (!window.confirm("Bu teklif silinsin mi?")) return;
    const { error } = await supabase.from("quote_requests").delete().eq("id", q.id);
    if (error) toast.error(error.message);
    else {
      setQuotes((qs) => qs.filter((x) => x.id !== q.id));
      toast.success("Teklif silindi.");
    }
  }

  if (loading) return <div className="text-[#6B6B6B] text-sm py-10" data-testid="quotes-loading">Yükleniyor...</div>;

  if (quotes.length === 0) {
    return (
      <div className="text-center py-16 text-[#6B6B6B]" data-testid="quotes-empty">
        <MailOpen className="w-10 h-10 mx-auto text-[#B09060]/40" strokeWidth={1.25} />
        <p className="mt-3 text-sm">Henüz teklif talebi yok.</p>
      </div>
    );
  }

  return (
    <div data-testid="quotes-section">
      <h2 className="font-serif-display text-2xl text-[#404040]">Teklif Talepleri</h2>
      <p className="text-sm text-[#6B6B6B] mt-1">{quotes.length} kayıt</p>

      <div className="space-y-3 mt-6">
        {quotes.map((q) => {
          const status = q.status || "yeni";
          const open = expanded === q.id;
          return (
            <div
              key={q.id}
              data-testid={`quote-row-${q.id}`}
              className="bg-white border border-[#B09060]/20 rounded-sm shadow-[0_4px_20px_rgba(176,144,96,0.05)]"
            >
              <button
                className="w-full text-left p-5 flex flex-wrap items-center gap-3"
                onClick={() => setExpanded(open ? null : q.id)}
                data-testid={`quote-expand-${q.id}`}
              >
                <div className="flex-1 min-w-48">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-[#404040] text-sm">{q.company_name}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[status] || STATUS_STYLE.yeni}`} data-testid={`quote-status-${q.id}`}>
                      {status}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full bg-[#F2F3EB] text-[#6B6B6B] text-[10px] uppercase tracking-wider">
                      {TYPE_LABELS[q.form_type] || q.form_type}
                    </span>
                  </div>
                  <div className="text-xs text-[#6B6B6B] mt-1">
                    {q.contact_name} · {q.phone} {q.email ? `· ${q.email}` : ""}
                    {q.vehicle_interest ? ` · ${q.vehicle_interest}` : ""}
                  </div>
                </div>
                <div className="text-[11px] text-[#9B9B8F]">
                  {new Date(q.created_at).toLocaleString("tr-TR")}
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-[#B09060]" /> : <ChevronDown className="w-4 h-4 text-[#B09060]" />}
              </button>

              {open && (
                <div className="px-5 pb-5 border-t border-[#B09060]/10 pt-4 space-y-4" data-testid={`quote-detail-${q.id}`}>
                  {q.message && <p className="text-sm text-[#404040] leading-relaxed">{q.message}</p>}

                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#8F7648] mb-1.5">Durum</label>
                    <div className="flex flex-wrap gap-2">
                      {STATUS.map((s) => (
                        <button
                          key={s}
                          onClick={() => patch(q, { status: s, is_read: true })}
                          data-testid={`quote-status-btn-${q.id}-${s}`}
                          className={`px-3.5 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider transition-colors ${
                            status === s
                              ? "bg-[#B09060] text-white"
                              : "border border-[#B09060]/30 text-[#8F7648] hover:bg-[#F2F3EB]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#8F7648] mb-1.5">Teklif Notları</label>
                    <textarea
                      rows={2}
                      defaultValue={q.admin_notes || ""}
                      data-testid={`quote-notes-${q.id}`}
                      placeholder="Görüşme notları, verilen fiyat..."
                      className="w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-3 py-2 text-sm text-[#404040] focus:outline-none focus:border-[#B09060]"
                      onBlur={(e) => e.target.value !== (q.admin_notes || "") && patch(q, { admin_notes: e.target.value })}
                    />
                  </div>

                  <div className="flex gap-2">
                    {status === "yeni" && (
                      <button
                        onClick={() => patch(q, { is_read: true })}
                        data-testid={`quote-read-${q.id}`}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-sm border border-[#B09060]/30 text-[#8F7648] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors"
                      >
                        <Check className="w-3.5 h-3.5" /> Okundu
                      </button>
                    )}
                    <button
                      onClick={() => remove(q)}
                      data-testid={`quote-delete-${q.id}`}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-sm border border-red-200 text-red-600 text-xs font-semibold uppercase tracking-wider hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Sil
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
